import { useEffect, useRef, useState } from "react";

const EASE_OUT_CUBIC_EXPONENT = 3;

export type AnimatedNumberProps = {
  value: number;
  /** Darstellung jedes Zwischenwerts (z. B. lokales Zahlenformat des Produkts); Standard: gerundete Zahl. */
  format?: (value: number) => string;
  /** Dauer des Hochzählens in Millisekunden. */
  duration?: number;
  className?: string;
};

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, EASE_OUT_CUBIC_EXPONENT);

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

/**
 * Zählt eine Kennzahl beim ersten Zeigen von 0 und bei Änderungen vom bisherigen Wert aus hoch.
 * Screenreader lesen nur den Endwert; reduzierte Bewegung zeigt ihn sofort.
 */
export function AnimatedNumber({ value, format = (v) => String(Math.round(v)), duration = 900, className }: AnimatedNumberProps) {
  const reduced = prefersReducedMotion();
  const [shown, setShown] = useState(0);
  const shownRef = useRef(0);

  useEffect(() => {
    if (reduced || !Number.isFinite(value) || shownRef.current === value) return;
    const from = shownRef.current;
    let frame = 0;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      shownRef.current = t >= 1 ? value : from + (value - from) * easeOutCubic(t);
      setShown(shownRef.current);
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, duration, reduced]);

  return (
    <span className={["mq-animated-number", className].filter(Boolean).join(" ")}>
      <span aria-hidden="true">{format(reduced ? value : shown)}</span>
      <span className="mq-visually-hidden">{format(value)}</span>
    </span>
  );
}
